import { useState, useEffect, useCallback, useRef } from 'react';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import './TargetPriceWidget.css';
import InfoPanel from '../../Components/Common/InfoPanel/InfoPanel';
import { TargetPriceType } from '../../types/company.type';
import { ReactComponent as ArrowUp } from '../../assets/icons/arrow-up.svg';
import { ReactComponent as TargetPriceInfoIcon } from '../../assets/icons/tooltip-company-score-icon.svg';
import { formatNumber } from '../../utils/utils';
import TargetPriceChart from '../../Components/TargetChart/TargetChart';
import { BaseWidgetProps, WidgetHeightMessage } from '../../types/common.type';
import {
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import Loading from '../../Components/Loading/Loading';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import { getTargetPrice } from '../../api/company.api';
import Layout from '../../Components/Common/Layout/Layout';

const TARGET_PRICE_INFO_TITLE = 'About target prices';

const TARGET_PRICE_INFO_CONTENT = (
  <>
    <p>
      Consensus broker estimates combine three key metrics: the statistical mean
      (average) using the majority accounting basis, along with the highest
      estimate (upside potential) and lowest estimate (downside risk). Comparing
      this consensus target to the security’s last close price reveals the
      expected percentage upside or downside for the share price.
    </p>
  </>
);

export interface TargetPriceWidgetProps extends BaseWidgetProps {
  token?: string;
  symbol?: string;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const TargetPriceWidget = (props: TargetPriceWidgetProps) => {
  const { onHeightChange, token, symbol, onNoData, onError } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  useResizeNotifier(containerRef, WidgetNameSource.TargetPrice, onHeightChange);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onNoDataRef = useRef(onNoData);
  onNoDataRef.current = onNoData;

  const touch = matchMedia('(hover: none)').matches;
  const [error, setError] = useState<string | null>(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'TargetPrice');
  const [data, setData] = useState<TargetPriceType>({
    name: '-',
    currentPrice: 0,
    targetPrice: 0,
    separationPrice: 0,
    separationPercent: 0,
    high: 0,
    low: 0,
    currency: 'USD',
    dp: 2,
  });

  // Call API when page changes
  useEffect(() => {
    fetchRecommendationData();
  }, [token, symbol]);

  const fetchRecommendationData = useCallback(async () => {
    setIsLoading(true);

    setError(null);
    setIsEmptyData(false);

    try {
      const res = await getTargetPrice(symbol || '', token || '');

      if (res.data && res.data[0].mean) {
        setData((prev) => ({
          ...prev,
          name: res.data[0].ticker,
          currentPrice: res.data[0].close_price ? res.data[0].close_price : 0,
          targetPrice: res.data[0].mean ? res.data[0].mean : 0,
          separationPrice: res.data[0].divergence ? res.data[0].divergence : 0,
          separationPercent: res.data[0].divergence
            ? res.data[0].divergence
            : 0,
          high: res.data[0].high ? res.data[0].high : 0,
          low: res.data[0].low ? res.data[0].low : 0,
          currency: res.data[0].close_price_ccy,
          dp: res.data[0].dp ?? 2,
        }));
      } else {
        setIsEmptyData(true);
        onNoDataRef.current?.({
          type: WidgetMessageType.NoData,
          source: 'TargetPriceWidget',
          data: null,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch recommendation data');
      console.error('Error fetching recommendation data:', err);
      onErrorRef.current?.({
        message: err.message,
        type: err.name + '_' + err.code,
      });
      setIsEmptyData(true);
    } finally {
      setIsLoading(false);
    }
  }, [token, symbol]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout theme={props?.theme || ThemeType.Direct}>
      <div ref={containerRef} className="targetPrice-container">
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="targetPrice">
            <div className="targetPrice-card-container">
              <div className="targetPrice-card-header">
                <div className="targetPrice-card-price-wrapper">
                  <span className="targetPrice-card-price">
                    {data.targetPrice
                      ? formatNumber(
                          data.targetPrice.toString(),
                          data.dp,
                          data.dp,
                        )
                      : '-'}
                  </span>
                  <span className="targetPrice-card-price-unit">
                    {data.currency ? data.currency : ''}
                  </span>
                </div>
                <button
                  type="button"
                  className="targetPrice-info-button"
                  aria-label={TARGET_PRICE_INFO_TITLE}
                  onClick={() => setIsInfoOpen(true)}
                >
                  <TargetPriceInfoIcon />
                </button>
              </div>
              <div className={`targetPrice-card-potential-container`}>
                {data.separationPrice > 0 || data.separationPrice < 0 ? (
                  <ArrowUp
                    className={`targetPrice-card-potential-icon${data.separationPrice > 0 ? '-up' : '-down'}`}
                    style={{
                      transform:
                        data.separationPrice > 0
                          ? 'rotate(0)'
                          : 'rotate(180deg)',
                    }}
                  />
                ) : (
                  ''
                )}
                <div
                  className={`targetPrice-card-potential ${data.separationPrice > 0 ? 'up' : 'down'}`}
                >
                  {data.separationPrice > 0
                    ? 'Upside Potential of '
                    : data.separationPrice < 0
                      ? 'Downside Potential of '
                      : '-'}

                  <span
                    className={`targetPrice-card-price-percentage ${data.separationPrice > 0 ? 'up' : 'down'}`}
                  >
                    {formatNumber(
                      data.separationPercent.toString(),
                      2,
                      2,
                      false,
                      true,
                      false,
                      '%',
                    )}
                  </span>
                </div>
              </div>
              <div className="targetPrice-card-content">
                <div className="targetPrice-card-description">
                  {`${data.name} has an average price target of ${formatNumber(data.targetPrice.toString(), data.dp, data.dp)}. The previous close price is ${formatNumber(data.currentPrice.toString(), data.dp, data.dp)}, indicating a potential ${data.separationPrice > 0 ? 'upside' : 'downside'} of ${formatNumber(
                    data.separationPercent.toString(),
                    2,
                    2,
                    false,
                    false,
                    false,
                    '%',
                  )} ${data.separationPrice > 0 ? 'above the target price.' : 'below the target price.'}`}
                </div>
              </div>
            </div>
            <div className="targetPrice-chart-container">
              <TargetPriceChart
                now={data.currentPrice}
                high={data.high}
                low={data.low}
                avg={data.targetPrice}
                dp={data.dp}
              />
            </div>
            {isInfoOpen && (
              <InfoPanel
                title={TARGET_PRICE_INFO_TITLE}
                onClose={() => setIsInfoOpen(false)}
              >
                {TARGET_PRICE_INFO_CONTENT}
              </InfoPanel>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TargetPriceWidget;
